/**
 * FinSummaryTile — single-line label + value row used inside the Financial
 * Summary side card. Label as caption, value as body1 (16px — the hierarchy
 * ceiling per §11b2 / portals.md — the previous StatCard rendered values
 * larger than the page-title h5 and was flagged in the design spec).
 *
 * Color the value via the `tone` prop (positive / negative / neutral) so net
 * worth swings dark/red without raw hex literals.
 */

import { alpha, Box, Typography, type TypographyProps, useTheme } from '@ops-matrix/design-system-ui';
import type { ReactElement, ReactNode } from 'react';

export interface FinSummaryTileProps {
  icon?: ReactNode;
  label: ReactNode;
  value: ReactNode;
  /** Color tone for the value. */
  tone?: 'positive' | 'negative' | 'neutral';
  /** Skeleton state — render placeholder dashes. */
  loading?: boolean;
  /** Hide bottom border for the last tile in a list. */
  isLast?: boolean;
}

export function FinSummaryTile({
  icon,
  label,
  value,
  tone = 'neutral',
  loading,
  isLast,
}: FinSummaryTileProps): ReactElement {
  const theme = useTheme();
  const toneColor: TypographyProps['color'] =
    tone === 'positive' ? 'success.main' : tone === 'negative' ? 'error.main' : 'text.primary';
  // P7: tone-mapped icon background instead of neutral action.hover — matches
  // the design's colored icon badges (mint/amber/red/id) so each row's value
  // and its leading icon read as the same semantic.
  const iconBg =
    tone === 'positive'
      ? alpha(theme.palette.success.main, 0.12)
      : tone === 'negative'
        ? alpha(theme.palette.error.main, 0.12)
        : theme.palette.action.hover;
  const iconColor = tone === 'positive' ? 'success.main' : tone === 'negative' ? 'error.main' : 'text.secondary';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.25,
        minHeight: 44,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: iconBg,
              color: iconColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 600, color: toneColor, fontVariantNumeric: 'tabular-nums' }}>
        {loading ? '—' : value}
      </Typography>
    </Box>
  );
}

export default FinSummaryTile;
